import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { apiGet, fmtDate } from "@/lib/shieldx";
import { PageHeader, Loader, Panel } from "@/components/shieldx/Primitives";
import { Table } from "@/components/shieldx/Table";
import { ShieldCheck, ShieldAlert, FileLock2 } from "lucide-react";

export default function SAAudit() {
  const [rows, setRows] = useState(null);
  const [verify, setVerify] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    const p = new URLSearchParams();
    if (filter) p.set("action", filter);
    const [a, v] = await Promise.all([apiGet(`/super-admin/audit?${p.toString()}`), apiGet("/super-admin/audit/verify")]);
    setRows(a); setVerify(v);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  if (!rows || !verify) return <Loader label="Loading tamper-evident audit chain" />;

  const columns = [
    { key: "seq", label: "#", render: (r) => <span className="font-mono text-xs text-slate-500">{r.seq}</span> },
    { key: "action", label: "Action", render: (r) => <span className="font-mono text-xs font-semibold text-slate-200">{r.action}</span> },
    { key: "target_type", label: "Target", render: (r) => <div><span className="text-xs text-slate-300">{r.target_type}</span><p className="font-mono text-[10px] text-slate-600">{r.target_id}</p></div> },
    { key: "actor", label: "Actor", render: (r) => <span className="text-xs text-slate-400">{r.actor}</span> },
    { key: "reason", label: "Reason", render: (r) => <span className="text-xs text-slate-400">{r.reason}</span> },
    { key: "result", label: "Result", render: (r) => <span className={r.result === "SUCCESS" ? "text-emerald-400 text-xs" : "text-rose-400 text-xs"}>{r.result}</span> },
    { key: "timestamp", label: "When", render: (r) => <span className="text-xs text-slate-500">{fmtDate(r.timestamp)}</span> },
    { key: "evidence_hash", label: "Evidence", render: (r) => <span className="font-mono text-[10px] text-slate-600">{(r.evidence_hash || "").slice(0, 12)}…</span> },
  ];

  return (
    <div>
      <PageHeader icon={FileLock2} title="Super Admin Audit" subtitle="Every privileged action, hash-chained and tamper-evident. Secrets are never recorded." />
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className={`mb-6 flex items-center gap-3 rounded-xl border p-4 ${verify.intact ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/10"}`} data-testid="audit-integrity">
        {verify.intact ? <ShieldCheck className="h-6 w-6 text-emerald-400" /> : <ShieldAlert className="h-6 w-6 text-rose-400" />}
        <div>
          <p className={`font-display text-sm font-semibold ${verify.intact ? "text-emerald-300" : "text-rose-300"}`}>
            {verify.intact ? "Audit chain INTACT" : "TAMPER DETECTED"}
          </p>
          <p className="text-xs text-slate-400">{verify.total} chained events verified{verify.broken_at_seq ? ` · broken at seq ${verify.broken_at_seq}` : " · all evidence hashes consistent"}.</p>
        </div>
      </motion.div>

      <Panel title="Audit Events" actions={
        <select data-testid="audit-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="h-8 rounded-lg border border-slate-800 bg-slate-900/70 px-2 text-xs text-slate-300 outline-none">
          <option value="">All actions</option>
          {["EDIT", "VERIFY", "SUSPEND", "REVOKE", "REACTIVATE", "APPROVE", "CHANGE_ROLE", "STEP_UP", "SETTINGS_UPDATE", "EMAIL_TEST_CONNECTION", "INVITATION_CREATE"].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      }>
        <Table columns={columns} rows={rows} testid="sa-audit-table" empty="No audit events yet" />
      </Panel>
    </div>
  );
}
